import {
  VAULT_ASSET_TYPE_SLUGS,
  type VaultAssetTypeSlug,
  isVaultAssetTypeSlug,
  vaultAssetTypeSectionKey,
} from "@project/core";

/** Alias local: mismos slugs que en BD / `@project/core`. */
export const VAULT_CATEGORY_SLUGS = VAULT_ASSET_TYPE_SLUGS;
export type VaultCategorySlug = VaultAssetTypeSlug;

export function isVaultCategorySlug(s: string): s is VaultCategorySlug {
  return isVaultAssetTypeSlug(s);
}

/** Clave de sección en listado (slug o `__none__`). */
export function normalizeVaultSectionKey(raw: string | null | undefined): string {
  return vaultAssetTypeSectionKey(raw);
}

/** Etiqueta de categoría para UI: slug de BD → i18n; valor desconocido → texto tal cual. */
export function vaultCategoryDisplayLabel(
  t: (key: string) => string,
  slug: string | null | undefined,
): string | null {
  if (!slug?.trim()) return null;
  const s = slug.trim();
  if (isVaultCategorySlug(s)) return t(`dashboardUi.vault.categories.${s}`);
  return s;
}

function todayLocalYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseYmd(ymd: string): [number, number, number] | null {
  const parts = ymd.split("-");
  if (parts.length !== 3) return null;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (![y, m, d].every((n) => Number.isFinite(n))) return null;
  return [y, m, d];
}

/** Días hasta el fin de garantía (negativo si ya venció). Fechas en calendario local. */
export function warrantyDaysRemaining(warrantyEndsOn: string): number {
  const today = todayLocalYmd();
  const tParts = parseYmd(today);
  const wParts = parseYmd(warrantyEndsOn);
  if (!tParts || !wParts) return 0;
  const [ty, tm, td] = tParts;
  const [wy, wm, wd] = wParts;
  const start = Date.UTC(ty, tm - 1, td);
  const end = Date.UTC(wy, wm - 1, wd);
  return Math.round((end - start) / 86_400_000);
}

export type WarrantyUrgency = "expired" | "urgent" | "warning" | "safe" | "none";

export function warrantyUrgency(warrantyEndsOn: string | null | undefined): WarrantyUrgency {
  if (!warrantyEndsOn) return "none";
  const d = warrantyDaysRemaining(warrantyEndsOn);
  if (d < 0) return "expired";
  if (d <= 14) return "urgent";
  if (d <= 90) return "warning";
  return "safe";
}

/** Avance temporal entre compra y fin de garantía (0–100). */
export function warrantyElapsedPercent(
  purchaseDate: string | null | undefined,
  warrantyEndsOn: string | null | undefined,
): number {
  if (!purchaseDate || !warrantyEndsOn) return 0;
  const pParts = parseYmd(purchaseDate);
  const wParts = parseYmd(warrantyEndsOn);
  if (!pParts || !wParts) return 0;
  const [py, pm, pd] = pParts;
  const [wy, wm, wd] = wParts;
  const start = Date.UTC(py, pm - 1, pd);
  const end = Date.UTC(wy, wm - 1, wd);
  const now = Date.now();
  if (end <= start) return 100;
  const p = ((now - start) / (end - start)) * 100;
  return Math.min(100, Math.max(0, p));
}
